/**
 * Componente: SystemAlertsPanel
 * Painel de alertas do sistema
 */

import React from 'react';
import { AlertCircle, TrendingUp, CheckCircle2, Loader2, Info } from 'lucide-react';

export interface Alert {
  id: string;
  type: 'urgent' | 'warning' | 'info' | 'success';
  title: string;
  description?: string;
  timeAgo: string;
  action?: string;
}

interface SystemAlertsPanelProps {
  alerts?: Alert[];
  isLoading?: boolean;
}

const alertConfig = {
  urgent: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-900 dark:text-red-100',
    title: 'text-red-900 dark:text-red-100',
    icon: 'text-red-600 dark:text-red-400',
    badge: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    dot: 'bg-red-600',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    text: 'text-amber-900 dark:text-amber-100',
    title: 'text-amber-900 dark:text-amber-100',
    icon: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-600',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-900 dark:text-blue-100',
    title: 'text-blue-900 dark:text-blue-100',
    icon: 'text-blue-600 dark:text-blue-400',
    badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    dot: 'bg-blue-600',
  },
  success: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-900 dark:text-green-100',
    title: 'text-green-900 dark:text-green-100',
    icon: 'text-green-600 dark:text-green-400',
    badge: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    dot: 'bg-green-600',
  },
};

export function SystemAlertsPanel({ alerts = [], isLoading = false }: SystemAlertsPanelProps) {
  const getAlertIcon = (type: Alert['type']) => {
    switch (type) {
      case 'urgent':
        return <AlertCircle className="w-5 h-5" />;
      case 'warning':
        return <TrendingUp className="w-5 h-5" />;
      case 'success':
        return <CheckCircle2 className="w-5 h-5" />;
      case 'info':
        return <Info className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Notificações do Sistema
        </h3>
        <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800 text-xs font-medium text-neutral-700 dark:text-neutral-300">
          <span className="w-2 h-2 bg-emerald-500 rounded-full" />
          Sistema Operacional
        </div>
      </div>

      <div className="space-y-3">
        {isLoading ? (
          <div className="flex py-12 items-center justify-center">
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="flex py-12 items-center justify-center">
            <p className="text-sm text-gray-500">Sem novas notificações no momento.</p>
          </div>
        ) : (
          alerts.map((alert) => {
            const config = alertConfig[alert.type];
            return (
              <div
                key={alert.id}
                className={`rounded-lg p-4 border ${config.bg} ${config.border}`}
              >
                <div className="flex gap-3">
                  <div className={`flex-shrink-0 ${config.icon}`}>
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className={`font-medium text-sm ${config.title}`}>
                          {alert.title}
                        </h4>
                        {alert.description && (
                          <p className={`text-xs mt-1 ${config.text} opacity-75`}>
                            {alert.description}
                          </p>
                        )}
                      </div>
                      <span className={`text-xs font-medium ${config.text} opacity-60 flex-shrink-0`}>
                        {alert.timeAgo}
                      </span>
                    </div>
                    {alert.action && (
                      <button className={`mt-3 text-xs font-medium px-3 py-1.5 rounded border ${config.badge} hover:opacity-80 transition-opacity`}>
                        {alert.action}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
